const API_INDEX = "/api/docs/index.json";

const state = {
  query: "",
  selectedId: "",
  docs: [],
  groups: [],
  cache: new Map(),
  requestId: 0,
};

const header = document.querySelector("[data-header]");
const docSearch = document.querySelector("[data-doc-search]");
const docList = document.querySelector("[data-doc-list]");
const docReader = document.querySelector("[data-doc-reader]");

const setHeaderState = () => {
  if (!header) return;
  header.classList.toggle("is-scrolled", window.scrollY > 8);
};

setHeaderState();
window.addEventListener("scroll", setHeaderState, { passive: true });

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const inlineMarkdown = (value) => {
  let output = escapeHtml(value);
  output = output.replace(/`([^`]+)`/g, "<code>$1</code>");
  output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  output = output.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  output = output.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  return output;
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/`/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const isTableSeparator = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
const isBlockStart = (line) =>
  /^(#{1,6})\s+/.test(line) ||
  /^```/.test(line) ||
  /^>\s?/.test(line) ||
  /^[-*]\s+/.test(line) ||
  /^\d+\.\s+/.test(line) ||
  /^\|/.test(line);

function renderTable(lines, start) {
  const rows = [];
  let index = start;
  while (index < lines.length && /^\|/.test(lines[index].trim())) {
    if (!isTableSeparator(lines[index])) {
      rows.push(
        lines[index]
          .trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((cell) => cell.trim()),
      );
    }
    index += 1;
  }

  if (!rows.length) return { html: "", next: index };

  const [head, ...body] = rows;
  const thead = `<thead><tr>${head.map((cell) => `<th>${inlineMarkdown(cell)}</th>`).join("")}</tr></thead>`;
  const tbody = `<tbody>${body
    .map((row) => `<tr>${row.map((cell) => `<td>${inlineMarkdown(cell)}</td>`).join("")}</tr>`)
    .join("")}</tbody>`;
  return { html: `<div class="doc-table-wrap"><table>${thead}${tbody}</table></div>`, next: index };
}

function renderList(lines, start, ordered) {
  const tag = ordered ? "ol" : "ul";
  const pattern = ordered ? /^\d+\.\s+(.+)$/ : /^[-*]\s+(.+)$/;
  const items = [];
  let index = start;
  while (index < lines.length) {
    const match = lines[index].match(pattern);
    if (!match) break;
    items.push(`<li>${inlineMarkdown(match[1])}</li>`);
    index += 1;
  }
  return { html: `<${tag}>${items.join("")}</${tag}>`, next: index };
}

function renderMarkdown(markdown) {
  const lines = String(markdown ?? "").split("\n");
  const html = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed || trimmed === "---") {
      index += 1;
      continue;
    }

    if (trimmed.startsWith("```")) {
      const lang = trimmed.slice(3).trim();
      const codeLines = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      html.push(
        `<pre class="doc-code"${lang ? ` data-lang="${escapeHtml(lang)}"` : ""}><code>${escapeHtml(
          codeLines.join("\n"),
        )}</code></pre>`,
      );
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 4);
      const title = heading[2].trim();
      html.push(`<h${level} id="${slugify(title)}">${inlineMarkdown(title)}</h${level}>`);
      index += 1;
      continue;
    }

    if (trimmed.startsWith(">")) {
      const notes = [];
      while (index < lines.length && lines[index].trim().startsWith(">")) {
        notes.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      html.push(`<p class="doc-note">${inlineMarkdown(notes.join(" "))}</p>`);
      continue;
    }

    if (/^\|/.test(trimmed) && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const table = renderTable(lines, index);
      html.push(table.html);
      index = table.next;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const list = renderList(lines, index, false);
      html.push(list.html);
      index = list.next;
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const list = renderList(lines, index, true);
      html.push(list.html);
      index = list.next;
      continue;
    }

    const paragraph = [trimmed];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines[index].trim())) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    html.push(`<p>${inlineMarkdown(paragraph.join(" "))}</p>`);
  }

  return html.join("");
}

function showReaderState(title, body) {
  if (!docReader) return;
  docReader.innerHTML = `
    <div class="docs-reader-empty">
      <h2>${escapeHtml(title)}</h2>
      <p>${escapeHtml(body)}</p>
    </div>
  `;
}

function filteredDocs() {
  const query = state.query.trim().toLowerCase();
  if (!query) return state.docs;
  return state.docs.filter((doc) =>
    [doc.title, doc.filename, doc.scope, doc.summary, doc.groupLabel]
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
}

function docsByGroup(docs) {
  const byGroup = new Map();
  for (const group of state.groups) {
    byGroup.set(group.id, { label: group.label, docs: [] });
  }
  for (const doc of docs) {
    if (!byGroup.has(doc.group)) {
      byGroup.set(doc.group, { label: doc.groupLabel, docs: [] });
    }
    byGroup.get(doc.group).docs.push(doc);
  }
  return [...byGroup.values()].filter((group) => group.docs.length > 0);
}

function renderDocList() {
  if (!docList) return;
  const docs = filteredDocs();
  if (!docs.length) {
    docList.innerHTML = `<p class="docs-empty">No AIR docs match this search.</p>`;
    return;
  }

  if (state.query.trim()) {
    docList.innerHTML = `
      <section class="doc-nav-group">
        <h2>Search results</h2>
        ${docs.map(renderDocNavItem).join("")}
      </section>
    `;
    return;
  }

  docList.innerHTML = docsByGroup(docs)
    .map(
      (group) => `
        <section class="doc-nav-group">
          <h2>${escapeHtml(group.label)}</h2>
          ${group.docs.map(renderDocNavItem).join("")}
        </section>
      `,
    )
    .join("");
}

function renderDocNavItem(doc) {
  return `
    <button type="button" class="doc-list-item ${doc.id === state.selectedId ? "active" : ""}" data-doc-id="${escapeHtml(
      doc.id,
    )}">
      <span>${escapeHtml(doc.number).padStart(2, "0")}</span>
      <strong>${escapeHtml(doc.title)}</strong>
    </button>
  `;
}

function renderDocReader(doc) {
  if (!docReader) return;
  const bodyMarkdown = String(doc.markdown ?? "").replace(/^#\s+.+\n+/, "");
  const toc = (doc.headings ?? [])
    .filter((heading) => heading.level === 2)
    .slice(0, 10)
    .map(
      (heading) =>
        `<button type="button" data-scroll-heading="${escapeHtml(heading.id)}">${escapeHtml(heading.title)}</button>`,
    )
    .join("");

  docReader.innerHTML = `
    <header class="doc-reader-header">
      <div>
        <p class="eyebrow">${escapeHtml(doc.groupLabel)}</p>
        <h1>${escapeHtml(doc.title)}</h1>
        <p>${inlineMarkdown(doc.scope || doc.summary)}</p>
      </div>
      <a href="${escapeHtml(doc.apiPath)}">JSON</a>
    </header>
    ${toc ? `<nav class="doc-toc" aria-label="Document table of contents">${toc}</nav>` : ""}
    <div class="doc-content">${renderMarkdown(bodyMarkdown)}</div>
  `;
  document.title = `${doc.title} - AIR Docs`;
}

async function fetchJson(path) {
  const response = await fetch(path, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Request failed: ${response.status} ${path}`);
  return response.json();
}

async function loadDoc(id) {
  if (state.cache.has(id)) return state.cache.get(id);
  const meta = state.docs.find((doc) => doc.id === id);
  if (!meta) throw new Error(`Unknown document: ${id}`);
  const doc = await fetchJson(meta.apiPath);
  state.cache.set(id, doc);
  return doc;
}

async function selectDoc(id, options = {}) {
  if (!state.docs.some((doc) => doc.id === id)) return;
  const requestId = ++state.requestId;
  state.selectedId = id;
  renderDocList();
  showReaderState("Loading document", `Fetching ${id}.json.`);

  try {
    const doc = await loadDoc(id);
    if (requestId !== state.requestId) return;
    renderDocReader(doc);
    if (options.updateHash !== false) history.replaceState(null, "", `#${id}`);
  } catch (error) {
    if (requestId !== state.requestId) return;
    showReaderState("Unable to load document", error.message);
  }
}

async function initDocs() {
  showReaderState("Loading docs", "Fetching /api/docs/index.json.");
  try {
    const index = await fetchJson(API_INDEX);
    state.docs = index.docs ?? [];
    state.groups = index.groups ?? [];
    state.selectedId = location.hash.slice(1) || index.defaultDocId || state.docs[0]?.id || "";
    renderDocList();
    if (state.selectedId) await selectDoc(state.selectedId, { updateHash: Boolean(location.hash) });
  } catch (error) {
    showReaderState("Unable to load docs API", error.message);
  }
}

docSearch?.addEventListener("input", (event) => {
  state.query = event.target.value;
  renderDocList();
});

docList?.addEventListener("click", (event) => {
  const button = event.target.closest("[data-doc-id]");
  if (!button) return;
  selectDoc(button.dataset.docId);
  docReader?.scrollIntoView({ block: "start" });
});

docReader?.addEventListener("click", (event) => {
  const button = event.target.closest("[data-scroll-heading]");
  if (!button) return;
  const target = docReader.querySelector(`#${CSS.escape(button.dataset.scrollHeading)}`);
  target?.scrollIntoView({ block: "start", behavior: "smooth" });
});

window.addEventListener("hashchange", () => {
  const id = location.hash.slice(1);
  if (id) selectDoc(id, { updateHash: false });
});

initDocs();
